import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { FinanceOverview } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatDay, parseMoney } from './money';
import { CampaignProgress, EndDateField, FREQUENCY, money, PeriodChecks, PledgeSummary, type Campaign, type Pledge, type PledgePeriod } from './pledges-shared';

type LeaderCampaign = Campaign & { accountId: string | null; categoryId: string | null };
type Overview = {
  today: string;
  campaigns: LeaderCampaign[];
  pledges: (Pledge & { person: { personId: string; name: string } })[];
  review: { paymentId: string; pledgeId: string; campaign: string; person: string; dueOn: string; amountCents: number; hasFile: boolean; fileName: string | null }[];
};

/** Promesas en Finanzas: propósitos, promesas de cada persona, palomear entregas y revisar comprobantes. */
export function PledgesTab({ churchId, overview, canManage, onChange }: { churchId: string; overview: FinanceOverview; canManage: boolean; onChange: (m?: string) => void }) {
  const base = `/finance/churches/${churchId}`;
  const data = useAsync((signal) => api.get<Overview>(`${base}/pledges`, signal), [base, overview]);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const run = async (action: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      data.reload();
      onChange(ok);
      return true;
    } catch (e) {
      setMsg(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (data.status === 'loading') return <Loading />;
  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  const o = data.data;
  const ctx = { base, churchId, overview, canManage, busy, run, today: o.today };

  return (
    <div className="stack">
      {msg ? <Alert>{msg}</Alert> : null}
      {o.review.length ? (
        <Card title={`Comprobantes por revisar (${o.review.length})`}>
          <ul className="list">
            {o.review.map((r) => <ReviewRow key={r.paymentId} r={r} ctx={ctx} />)}
          </ul>
        </Card>
      ) : null}

      {o.campaigns.length === 0 && !creating ? (
        <EmptyState title="Aún no hay propósitos de promesas.">
          <p className="muted small">Ejemplo: «Mesas nuevas». Dentro de cada propósito registras quién prometió cuánto y palomeas cada entrega.</p>
        </EmptyState>
      ) : null}
      {canManage && !creating ? (
        <div className="actions">
          <Button onClick={() => setCreating(true)}>Nuevo propósito</Button>
        </div>
      ) : null}
      {creating ? (
        <Card title="Nuevo propósito">
          <CampaignForm ctx={ctx} onDone={() => setCreating(false)} />
        </Card>
      ) : null}

      {o.campaigns.map((c) => (
        <CampaignCard key={c.id} c={c} pledges={o.pledges.filter((p) => p.campaignId === c.id)} ctx={ctx} />
      ))}
    </div>
  );
}

type Ctx = { base: string; churchId: string; overview: FinanceOverview; canManage: boolean; busy: boolean; today: string; run: (action: () => Promise<unknown>, ok: string) => Promise<boolean> };

function ReviewRow({ r, ctx }: { r: Overview['review'][number]; ctx: Ctx }) {
  const [rejecting, setRejecting] = useState(false);
  return (
    <li className="list__item list__item--stack">
      <span>
        <strong>{r.person}</strong> · {r.campaign} · <span className="money">{money(r.amountCents)}</span> <span className="muted small">· entrega del {formatDay(r.dueOn)}</span>
      </span>
      {r.hasFile ? (
        <a className="small" href={`/api${ctx.base}/pledge-payments/${r.paymentId}/file`} target="_blank" rel="noreferrer">
          📎 Ver comprobante{r.fileName ? ` (${r.fileName})` : ''}
        </a>
      ) : null}
      {ctx.canManage ? (
        rejecting ? (
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const note = String(new FormData(e.currentTarget).get('note') ?? '').trim();
              void ctx.run(() => api.post(`${ctx.base}/pledge-payments/${r.paymentId}/reject`, { note }), 'Comprobante rechazado. Se le avisó a la persona.');
            }}
          >
            <Field label="Motivo (se le envía a la persona)" name="note" required minLength={3} maxLength={300} placeholder="No se ve el monto de la transferencia" />
            <div className="actions">
              <Button type="submit" variant="danger" busy={ctx.busy}>Rechazar</Button>
              <Button variant="ghost" onClick={() => setRejecting(false)}>Cancelar</Button>
            </div>
          </form>
        ) : (
          <div className="actions">
            <Button busy={ctx.busy} onClick={() => void ctx.run(() => api.post(`${ctx.base}/pledge-payments/${r.paymentId}/approve`), 'Entrega aprobada.')}>Aprobar</Button>
            <Button variant="ghost" onClick={() => setRejecting(true)}>Rechazar</Button>
          </div>
        )
      ) : null}
    </li>
  );
}

type Filter = 'todas' | 'atrasadas' | 'aldia' | 'revision' | 'cerradas';
const FILTERS: [Filter, string][] = [['todas', 'Activas'], ['atrasadas', 'Atrasadas'], ['revision', 'Por revisar'], ['aldia', 'Al día'], ['cerradas', 'Cumplidas o canceladas']];
const PER: Record<string, string> = { semanal: 'sem', quincenal: 'quinc.', mensual: 'mes', anual: 'año' };
const matches = (p: Pledge, f: Filter) =>
  f === 'cerradas' ? p.status !== 'activa'
  : p.status !== 'activa' ? false
  : f === 'atrasadas' ? p.lateCount > 0
  : f === 'revision' ? p.periods.some((x) => x.status === 'revision')
  : f === 'aldia' ? p.lateCount === 0
  : true;
const fold = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function CampaignCard({ c, pledges, ctx }: { c: LeaderCampaign; pledges: Overview['pledges']; ctx: Ctx }) {
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [details, setDetails] = useState(false);
  const [filter, setFilter] = useState<Filter>('todas');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const shown = pledges
    .filter((p) => matches(p, filter) && (!q.trim() || fold(p.person.name).includes(fold(q.trim()))))
    .sort((x, y) => x.person.name.localeCompare(y.person.name, 'es'));
  const count = (f: Filter) => pledges.filter((p) => matches(p, f)).length;
  return (
    <Card
      title={
        <>
          {c.name} <span className="badge">{c.visibility === 'publica' ? 'Pública' : 'Privada'}</span>
        </>
      }
      action={ctx.canManage && !editing ? <Button variant="ghost" onClick={() => setEditing(true)}>Editar</Button> : null}
    >
      {editing ? (
        <CampaignForm ctx={ctx} campaign={c} onDone={() => setEditing(false)} />
      ) : (
        <>
          <CampaignProgress c={c} />
          <button type="button" className="link-button small" onClick={() => setDetails(!details)}>
            {details ? 'Ocultar detalles' : 'Ver detalles y datos para transferir'}
          </button>
          {details ? (
            <div className="stack">
              {c.description ? <p className="small">{c.description}</p> : null}
              <p className="small muted">{c.visibility === 'publica' ? 'Los miembros pueden inscribirse solos desde «Promesas».' : 'Solo Finanzas agrega las promesas.'}</p>
              {c.transferInfo ? <p className="small pledge-transfer"><strong>Para transferir:</strong> {c.transferInfo}</p> : null}
              {!c.accountId ? <p className="small muted">Las entregas no se registran en el libro de Finanzas (no tiene cuenta y categoría).</p> : null}
            </div>
          ) : null}
        </>
      )}

      {ctx.canManage && !adding ? (
        <div className="actions">
          {!c.closed ? <Button variant="secondary" onClick={() => setAdding(true)}>Agregar promesa</Button> : null}
          <Button variant="ghost" busy={ctx.busy} onClick={() => (c.closed || window.confirm(`¿Cerrar «${c.name}»? Ya no se podrán agregar promesas; puedes volver a abrirlo.`)) && void ctx.run(() => api.post(`${ctx.base}/pledge-campaigns/${c.id}/closed`, { closed: !c.closed }), c.closed ? 'Propósito abierto otra vez.' : 'Propósito cerrado.')}>
            {c.closed ? 'Volver a abrir' : 'Cerrar propósito'}
          </Button>
        </div>
      ) : null}
      {ctx.canManage && adding ? <PledgeForm ctx={ctx} campaign={c} onDone={() => setAdding(false)} /> : null}

      {pledges.length ? (
        <div className="pledge-toolbar">
          <input type="search" className="pledge-search" placeholder="Buscar persona…" value={q} onChange={(e) => setQ(e.target.value)} aria-label={`Buscar persona en ${c.name}`} />
          <div className="pledge-filters" role="group" aria-label="Filtrar promesas">
            {FILTERS.map(([k, label]) => {
              const n = count(k);
              if (k !== 'todas' && n === 0) return null;
              return (
                <button key={k} type="button" className={`chip${filter === k ? ' chip--active' : ''}`} aria-pressed={filter === k} onClick={() => setFilter(k)}>
                  {label} {n}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="muted small">Aún no hay promesas.</p>
      )}

      {pledges.length && shown.length === 0 ? <p className="muted small">Nadie coincide con la búsqueda o el filtro.</p> : null}
      <ul className="pledge-rows">
        {shown.map((p) => <PledgeRow key={p.id} p={p} c={c} ctx={ctx} open={open === p.id} onToggle={() => setOpen(open === p.id ? null : p.id)} />)}
      </ul>

    </Card>
  );
}

/** Una fila corta por persona; al tocarla se abren sus fechas, editar y demás acciones. */
function PledgeRow({ p, c, ctx, open, onToggle }: { p: Overview['pledges'][number]; c: LeaderCampaign; ctx: Ctx; open: boolean; onToggle: () => void }) {
  const [editing, setEditing] = useState(false);
  const pending = p.periods.filter((x) => x.status === 'falta' || x.status === 'rechazado');
  const review = p.periods.filter((x) => x.status === 'revision').length;
  const firstPending = pending[0];
  const mark = (dueOn: string) => void ctx.run(() => api.post(`${ctx.base}/pledges/${p.id}/mark`, { dueOn }), `Entrega del ${formatDay(dueOn)} palomeada.`);
  const pick = (x: PledgePeriod) => {
    if (!ctx.canManage || p.status === 'cancelada') return;
    if (x.status === 'pagado' && x.paymentId) {
      if (window.confirm(`¿Quitar la entrega del ${formatDay(x.dueOn)}? Si se registró en Finanzas, el ingreso se revierte.`))
        void ctx.run(() => api.post(`${ctx.base}/pledge-payments/${x.paymentId}/undo`), 'Entrega quitada.');
    } else if (x.status === 'revision' && x.paymentId) {
      if (window.confirm(`${p.person.name} envió comprobante del ${formatDay(x.dueOn)}. ¿Aprobarlo?`))
        void ctx.run(() => api.post(`${ctx.base}/pledge-payments/${x.paymentId}/approve`), 'Entrega aprobada.');
    } else if (window.confirm(`¿Palomear la entrega del ${formatDay(x.dueOn)} de ${p.person.name} (${money(p.amountCents)})?`)) {
      mark(x.dueOn);
    }
  };
  const setStatus = (status: Pledge['status'], ok: string) => void ctx.run(() => api.post(`${ctx.base}/pledges/${p.id}/status`, { status, note: null }), ok);
  const state =
    p.status !== 'activa' ? <span className="badge">{p.status === 'cumplida' ? 'Cumplida' : 'Cancelada'}</span>
    : review ? <span className="badge badge--pending">Por revisar</span>
    : p.lateCount ? <span className="badge badge--late">{p.lateCount} pendiente{p.lateCount === 1 ? '' : 's'}</span>
    : <span className="badge badge--active">Al día</span>;
  return (
    <li className={`pledge-row${open ? ' pledge-row--open' : ''}`}>
      <div className="pledge-row__line">
        <button type="button" className="pledge-row__main" aria-expanded={open} onClick={onToggle}>
          <span className="pledge-row__name">{p.person.name}</span>
          <span className="pledge-row__meta small muted">
            {money(p.amountCents)}/{PER[p.frequency]} · lleva {money(p.paidCents)}
          </span>
        </button>
        {state}
        {ctx.canManage && p.status === 'activa' && firstPending && !open ? (
          <button type="button" className="pledge-quick" title={`Palomear ${formatDay(firstPending.dueOn)}`} aria-label={`Palomear la entrega del ${formatDay(firstPending.dueOn)} de ${p.person.name}`}
            disabled={ctx.busy} onClick={() => window.confirm(`¿Palomear la entrega del ${formatDay(firstPending.dueOn)} de ${p.person.name} (${money(p.amountCents)})?`) && mark(firstPending.dueOn)}>
            ✓
          </button>
        ) : null}
      </div>
      {open ? (
        <div className="pledge-row__body">
          {editing ? (
            <PledgeForm ctx={ctx} campaign={c} pledge={p} onDone={() => setEditing(false)} />
          ) : (
            <>
              <PledgeSummary p={p} />
              {p.note ? <p className="small muted">Nota: {p.note}</p> : null}
              {p.selfEnrolled ? <p className="small muted">Se inscribió solo.</p> : null}
              <PeriodChecks periods={p.periods} onPick={ctx.canManage && p.status !== 'cancelada' ? pick : undefined} label={(x) => (x.status === 'pagado' ? 'Quitar' : x.status === 'revision' ? 'Revisar' : 'Palomear')} />
              {ctx.canManage ? (
                <div className="actions">
                  <Button variant="secondary" onClick={() => setEditing(true)}>Editar</Button>
                  {p.status === 'activa' ? (
                    <>
                      <Button variant="ghost" busy={ctx.busy} onClick={() => setStatus('cumplida', 'Promesa cumplida.')}>Marcar cumplida</Button>
                      <Button variant="ghost" busy={ctx.busy} onClick={() => window.confirm(`¿Cancelar la promesa de ${p.person.name}?`) && setStatus('cancelada', 'Promesa cancelada.')}>Cancelar promesa</Button>
                    </>
                  ) : (
                    <Button variant="ghost" busy={ctx.busy} onClick={() => setStatus('activa', 'Promesa activa otra vez.')}>Volver a activar</Button>
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </li>
  );
}

const text = (f: FormData, k: string) => String(f.get(k) ?? '').trim();

function CampaignForm({ ctx, campaign: c, onDone }: { ctx: Ctx; campaign?: LeaderCampaign; onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const accounts = ctx.overview.accounts.filter((a) => !a.archived);
  const income = ctx.overview.categories.filter((x) => x.kind === 'income');
  const [visibility, setVisibility] = useState<'publica' | 'privada'>(c?.visibility ?? 'publica');
  const [noGoal, setNoGoal] = useState(c ? c.goalCents === null : false);
  const [noEnd, setNoEnd] = useState(c ? c.endsOn === null : true);
  const [account, setAccount] = useState(c ? c.accountId ?? '' : accounts[0]?.id ?? '');
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const goal = noGoal ? '' : text(f, 'goal');
    const goalCents = goal ? parseMoney(goal) : null;
    if (!noGoal && goalCents === null) return setError('Escribe la meta, por ejemplo 30,000, o marca «Sin meta».');
    const endsOn = noEnd ? null : text(f, 'endsOn') || null;
    if (!noEnd && !endsOn) return setError('Elige la fecha de fin o marca «Sin fecha de fin».');
    const body = {
      name: text(f, 'name'), description: text(f, 'description') || null, visibility, goalCents,
      startsOn: text(f, 'startsOn'), endsOn, accountId: account || null, categoryId: account ? text(f, 'category') || null : null, transferInfo: text(f, 'transferInfo') || null,
    };
    setError(null);
    void ctx.run(() => (c ? api.put(`${ctx.base}/pledge-campaigns/${c.id}`, body) : api.post(`${ctx.base}/pledge-campaigns`, body)), c ? 'Propósito guardado.' : 'Propósito creado.').then((ok) => ok && onDone());
  }
  return (
    <form className="form pledge-form" onSubmit={submit} noValidate>
      <fieldset className="pledge-form__section">
        <legend>1. ¿Para qué es?</legend>
        <Field label="Propósito" name="name" required maxLength={120} defaultValue={c?.name} placeholder="Comprar mesas nuevas" />
        <div className="field">
          <label htmlFor="pc-desc">Descripción (opcional)</label>
          <textarea id="pc-desc" name="description" maxLength={1000} rows={2} defaultValue={c?.description ?? ''} placeholder="Mesas para el salón de la Escuela Dominical" />
        </div>
      </fieldset>

      <fieldset className="pledge-form__section">
        <legend>2. ¿Quién puede inscribirse?</legend>
        <div className="pledge-options">
          {([
            ['publica', 'Pública', 'Los miembros se inscriben solos.'],
            ['privada', 'Privada', 'Solo Finanzas agrega las promesas.'],
          ] as const).map(([value, title, hint]) => (
            <label key={value} className={`pledge-option${visibility === value ? ' pledge-option--on' : ''}`}>
              <input type="radio" name="visibility" value={value} checked={visibility === value} onChange={() => setVisibility(value)} />
              <span>
                <strong>{title}</strong>
                <span className="small muted">{hint}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="pledge-form__section">
        <legend>3. Meta y fechas</legend>
        <div className="field">
          <label htmlFor="pc-goal">Meta total</label>
          <input id="pc-goal" name="goal" inputMode="decimal" disabled={noGoal} defaultValue={c?.goalCents ? String(c.goalCents / 100) : ''} placeholder={noGoal ? 'Sin meta' : '30,000'} />
          <label className="check small"><input type="checkbox" checked={noGoal} onChange={(e) => setNoGoal(e.target.checked)} /> Sin meta</label>
        </div>
        <div className="form__row">
          <Field label="A partir de" name="startsOn" type="date" required defaultValue={c?.startsOn ?? ctx.today} />
          <div className="field">
            <label htmlFor="pc-end">Hasta</label>
            <input id="pc-end" name="endsOn" type="date" disabled={noEnd} defaultValue={c?.endsOn ?? ''} />
            <label className="check small"><input type="checkbox" checked={noEnd} onChange={(e) => setNoEnd(e.target.checked)} /> Sin fecha de fin</label>
          </div>
        </div>
      </fieldset>

      <fieldset className="pledge-form__section">
        <legend>4. Dinero</legend>
        <div className="field">
          <label htmlFor="pc-acc">Registrar cada entrega en la cuenta</label>
          <select id="pc-acc" name="account" value={account} onChange={(e) => setAccount(e.target.value)}>
            <option value="">No registrar en Finanzas</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        {account ? (
          <div className="field">
            <label htmlFor="pc-cat">Como ingreso de</label>
            <select id="pc-cat" name="category" defaultValue={c?.categoryId ?? income.find((x) => x.name === 'Donativos')?.id ?? income[0]?.id}>
              {income.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </div>
        ) : null}
        <div className="field">
          <label htmlFor="pc-transfer">Datos para transferir (opcional)</label>
          <textarea id="pc-transfer" name="transferInfo" maxLength={600} rows={3} defaultValue={c?.transferInfo ?? ''} placeholder={'Banco: BBVA\nCLABE: 012 345 678901234567\nA nombre de: …'} />
          <small className="field__hint">Los miembros lo ven para transferir y luego suben su comprobante.</small>
        </div>
      </fieldset>

      {error ? <Alert>{error}</Alert> : null}
      <div className="actions">
        <Button type="submit" busy={ctx.busy}>{c ? 'Guardar' : 'Crear propósito'}</Button>
        <Button variant="ghost" onClick={onDone}>Cancelar</Button>
      </div>
    </form>
  );
}

function PledgeForm({ ctx, campaign: c, pledge, onDone }: { ctx: Ctx; campaign: LeaderCampaign; pledge?: Overview['pledges'][number]; onDone: () => void }) {
  const people = useAsync((signal) => api.get<{ items: { personId: string; name: string }[] }>(`${ctx.base}/pledge-people`, signal).then((r) => r.items), [ctx.base]);
  const [error, setError] = useState<string | null>(null);
  const key = pledge?.id ?? c.id;
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const amountCents = parseMoney(text(f, 'amount'));
    if (amountCents === null) return setError('Escribe una cantidad válida, por ejemplo 500.');
    if (!text(f, 'personId')) return setError('Elige a la persona.');
    setError(null);
    const body = { personId: text(f, 'personId'), amountCents, frequency: text(f, 'frequency'), startsOn: text(f, 'startsOn'), endsOn: text(f, 'endsOn') || null, note: text(f, 'note') || null };
    void ctx.run(
      () => (pledge ? api.put(`${ctx.base}/pledges/${pledge.id}`, body) : api.post(`${ctx.base}/pledges`, { ...body, campaignId: c.id })),
      pledge ? 'Promesa corregida.' : 'Promesa registrada. Se le avisó a la persona.',
    ).then((ok) => ok && onDone());
  }
  return (
    <form className="form pledge-form" onSubmit={submit} noValidate>
      {pledge ? <p className="small"><strong>Editar promesa</strong></p> : null}
      <div className="field">
        <label htmlFor={`pl-person-${key}`}>Persona</label>
        <select id={`pl-person-${key}`} name="personId" defaultValue={pledge?.personId ?? ''}>
          <option value="" disabled>{people.status === 'loading' ? 'Cargando…' : 'Elige…'}</option>
          {pledge && people.status !== 'ready' ? <option value={pledge.personId}>{pledge.person.name}</option> : null}
          {people.status === 'ready' ? people.data.map((p) => <option key={p.personId} value={p.personId}>{p.name}</option>) : null}
        </select>
      </div>
      <div className="form__row">
        <Field label="Cantidad" name="amount" inputMode="decimal" required placeholder="500" defaultValue={pledge ? String(pledge.amountCents / 100) : undefined} />
        <div className="field">
          <label htmlFor={`pl-freq-${key}`}>Frecuencia</label>
          <select id={`pl-freq-${key}`} name="frequency" defaultValue={pledge?.frequency ?? 'semanal'}>
            {Object.entries(FREQUENCY).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      </div>
      <div className="form__row">
        <Field label="A partir de" name="startsOn" type="date" required defaultValue={pledge?.startsOn ?? (ctx.today > c.startsOn ? ctx.today : c.startsOn)} />
        <EndDateField id={`pl-end-${key}`} defaultValue={pledge ? pledge.endsOn : c.endsOn} />
      </div>
      <Field label="Nota (opcional)" name="note" maxLength={300} defaultValue={pledge?.note ?? ''} />
      {pledge && pledge.paidCount > 0 ? <p className="small muted">Si cambias la frecuencia o la fecha de inicio, lo ya entregado se conserva en el total.</p> : null}
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions">
        <Button type="submit" busy={ctx.busy}>{pledge ? 'Guardar cambios' : 'Registrar promesa'}</Button>
        <Button variant="ghost" onClick={onDone}>Cancelar</Button>
      </div>
    </form>
  );
}
